import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Facebook, Instagram } from "lucide-react";

const LOGO_URL = "/logo.png";
const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";
const ADDRESS = "109 Henrietta St, Wichita Falls, TX 76301";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
  { name: "Get a Quote", path: "/quote" },
];

const businessHours = [
  { day: "Monday - Friday", hours: "9:30 AM - 5:00 PM" },
  { day: "Saturday", hours: "11:00 AM - 1:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-card border-t border-border">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" data-testid="footer-logo-link">
              <img
                src={LOGO_URL}
                alt="Tony's Auto Quality Service"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Your trusted local auto repair shop in Wichita Falls. Quality service, 
              honest pricing, and expert mechanics you can count on.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="social-icon"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl uppercase tracking-wide text-foreground mb-6">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="font-heading text-xl uppercase tracking-wide text-foreground mb-6">
              Business Hours
            </h4>
            <div className="space-y-3">
              {businessHours.map((item) => (
                <div key={item.day} className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground text-sm">{item.day}</p>
                    <p className="text-muted-foreground text-sm">{item.hours}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-xl uppercase tracking-wide text-foreground mb-6">
              Contact Us
            </h4>
            <div className="space-y-4">
              <a
                href={PHONE_LINK}
                data-testid="footer-phone-link"
                className="flex items-center gap-3 text-primary hover:text-blue-400 transition-colors duration-300"
              >
                <Phone className="w-5 h-5" />
                <span className="font-bold text-lg">{PHONE_NUMBER}</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <p className="text-muted-foreground text-sm">{ADDRESS}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm text-center md:text-left">
              © {currentYear} Tony's Auto Quality Service. All rights reserved.
            </p>
            <a
              href={PHONE_LINK}
              data-testid="footer-bottom-phone"
              className="text-primary hover:text-blue-400 font-bold transition-colors duration-300"
            >
              {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
